import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { AppInsightsService, ApplicationInsightsModule } from '@markpieszak/ng-application-insights';
import { BsDropdownModule, TimepickerModule, TypeaheadModule } from 'ngx-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';

import { AddressSearchComponent } from './components/address-search/address-search.component';
import { AppComponent } from './components/app.component';
import { CallbackComponent } from './components/callback/callback.component';
import { CreateVideoRequestComponent } from './components/create-video-request/create-video-request.component';
import { FlashMessagesForComponent } from './components/flash-messages-for/flash-messages-for.component';
import { FlashMessagesComponent } from './components/flash-messages/flash-messages.component';
import { FooterComponent } from './components/footer/footer.component';
import { GpsLogsComponent } from './components/gps-logs/gps-logs.component';
import { GpsSearchComponent } from './components/gps-search/gps-search.component';
import { HomeComponent } from './components/home/home.component';
import { NavTabsComponent } from './components/nav-tabs/nav-tabs.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { SearchResultTabsComponent } from './components/search-result-tabs/search-result-tabs.component';
import { SelectSiteComponent } from './components/select-site/select-site.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { UnitSearchComponent } from './components/unit-search/unit-search.component';
import { UsageReportsComponent } from './components/usage-reports/usage-reports.component';
import { VideoRequestComponent } from './components/video-request/video-request.component';
import { VideoRequestsComponent } from './components/video-requests/video-requests.component';
import { ErrorComponent } from './components/error/error.component';

import { CustomErrorHandler } from './custom-error-handler';

import { DateFromConfigPipe } from './pipes/date-from-config.pipe';
import { StatusLabelPipe } from './pipes/status-label.pipe';
import { IsoDateStringMomentFromConfigPipe } from './pipes/iso-date-string-moment-from-config.pipe';

import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { AddressService } from './services/address/address.service';
import { ApiRootInterceptorService } from './services/api-root-interceptor/api-root-interceptor.service';
import { AuthCallbackService } from './services/auth-callback/auth-callback.service';
import { AuthConfigService } from './services/auth-config/auth-config.service';
import { AuthGuardService } from './services/auth-guard/auth-guard.service';
import { AuthService } from './services/auth/auth.service';
import { CameraService } from './services/camera/camera.service';
import { ConfigService } from './services/config/config.service';
import { DateService } from './services/date/date.service';
import { FlashMessagesService } from './services/flash-messages/flash-messages.service';
import { GpsLogsService } from './services/gps-logs/gps-logs.service';
import { ProfileService } from './services/profile/profile.service';
import { ReturnUrlService } from './services/return-url/return-url.service';
import { SearchService } from './services/search/search.service';
import { SitesService } from './services/sites/sites.service';
import { SpinnerInterceptorService } from './services/spinner-interceptor/spinner-interceptor.service';
import { SpinnerService } from './services/spinner/spinner.service';
import { TermsAndConditionsService } from './services/terms-and-conditions/terms-and-conditions.service';
import { TrucksService } from './services/trucks/trucks.service';
import { UsageReportsService } from './services/usage-reports/usage-reports.service';
import { VideoAssetsService } from './services/video-assets/video-assets.service';
import { VideoRequestsService } from './services/video-requests/video-requests.service';
import { WindowReferenceService } from './services/window-reference/window-reference.service';
import { WindowService } from './services/window/window.service';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { UsageReportsAuthGuardService } from './services/auth-guard/usage-reports-auth-guard';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { MsalModule, MsalService, MsalGuard, MsalAngularConfiguration, MSAL_CONFIG, MSAL_CONFIG_ANGULAR } from '@azure/msal-angular';
import { Configuration } from 'msal';
import { AuthInterceptorService } from './services/auth-interceptor/auth-interceptor.service';
import { ConsentScopesService } from './services/config/consent-scopes.service';

// Move isIE definition before its usage in msalConfigFactory
const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function msalConfigFactory(configService: ConfigService): Configuration {
    return {
        auth: {
            clientId: configService.config.clientId,
            authority: configService.config.authority,
            validateAuthority: true,
            redirectUri: window.location.origin + '/callback',
            postLogoutRedirectUri: window.location.origin,
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: isIE,
        },
        framework: {
        }
    };
}

export function msalAngularConfigFactory(consentScopesService: ConsentScopesService, configService: ConfigService): MsalAngularConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    const apiRoot = configService.config.apiRoot.endsWith('/') ? configService.config.apiRoot : configService.config.apiRoot + '/';
    protectedResourceMap.set(`${apiRoot}*`, consentScopesService.getConsentScopes());

    return {
        popUp: false,
        consentScopes: consentScopesService.getConsentScopes(),
        protectedResourceMap
    };
}

const appRoutes: Routes = [
    {
        path: 'address-search',
        component: AddressSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Address Search" }
    },
    {
        path: 'address-search/:site',
        component: AddressSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Address Search" }
    },
    {
        path: 'callback',
        component: HomeComponent,
        canActivate: [MsalGuard],
        data: { roles: ['jjr', 'council'] }
    },
    {
        path: 'create-video-request/:truckNumber',
        component: CreateVideoRequestComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Create Request" }
    },
    {
        path: 'gps-search',
        component: GpsSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "GPS Search" }
    },
    {
        path: 'gps-search/:site',
        component: GpsSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "GPS Search" }
    },
    { path: 'home', component: HomeComponent },
    {
        path: 'unit-search',
        component: UnitSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr'], title: "Unit Search" }
    },
    {
        path: 'unit-search/:site',
        component: UnitSearchComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr'], title: "Unit Search" }
    },
    {
        path: 'video-request/:requestId',
        component: VideoRequestComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Video Request" }
    },
    {
        path: 'video-requests',
        component: VideoRequestsComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Video Requests List" }
    },
    {
        path: 'video-requests/:vrrId',
        component: VideoRequestsComponent,
        canActivate: [MsalGuard, AuthGuardService],
        data: { roles: ['jjr', 'council'], title: "Video Requests List" }
    },
    {
        path: 'usage-reports',
        component: UsageReportsComponent,
        canActivate: [MsalGuard, UsageReportsAuthGuardService],
    },
    {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
    },
    {
        path: 'terms-and-conditions',
        pathMatch: 'full',
        component: TermsAndConditionsComponent
    },
    { path: 'error', component: ErrorComponent },
    { path: '**', component: PageNotFoundComponent }
];

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AddressSearchComponent,
        AppComponent,
        CallbackComponent,
        CreateVideoRequestComponent,
        DateFromConfigPipe,
        StatusLabelPipe,
        FlashMessagesComponent,
        FlashMessagesForComponent,
        FooterComponent,
        GpsLogsComponent,
        GpsSearchComponent,
        HomeComponent,
        IsoDateStringMomentFromConfigPipe,
        NavbarComponent,
        NavTabsComponent,
        PageNotFoundComponent,
        SearchResultTabsComponent,
        SelectSiteComponent,
        SpinnerComponent,
        TermsAndConditionsComponent,
        UnitSearchComponent,
        UsageReportsComponent,
        VideoRequestComponent,
        VideoRequestsComponent,
        ErrorComponent
    ],
    imports: [
        BrowserModule,
        BsDropdownModule.forRoot(),
        FormsModule,
        HttpClientModule,
        RouterModule.forRoot(appRoutes,
            {
                enableTracing: false,
                onSameUrlNavigation: 'reload'
            }),
        TimepickerModule.forRoot(),
        TypeaheadModule.forRoot(),
        LeafletModule.forRoot(),
        ApplicationInsightsModule.forRoot({ instrumentationKeySetlater: true }),
        BsDatepickerModule.forRoot(),
        PopoverModule.forRoot(),
        NgSelectModule,
        MsalModule
    ],
    providers: [
        AddressService,
        AuthCallbackService,
        AuthConfigService,
        AuthGuardService,
        UsageReportsAuthGuardService,
        AuthService,
        CameraService,
        ConfigService,
        DateService,
        { provide: ErrorHandler, useClass: CustomErrorHandler },
        FlashMessagesService,
        GpsLogsService,
        ProfileService,
        ReturnUrlService,
        SearchService,
        SitesService,
        SpinnerService,
        TermsAndConditionsService,
        TrucksService,
        UsageReportsService,
        VideoAssetsService,
        VideoRequestsService,
        WindowReferenceService,
        WindowService,
        AppInsightsService,
        ConsentScopesService,
        MsalService,
        { provide: MSAL_CONFIG, useFactory: msalConfigFactory, deps: [ConfigService] },
        { provide: MSAL_CONFIG_ANGULAR, useFactory: msalAngularConfigFactory, deps: [ConsentScopesService, ConfigService] },
        { provide: HTTP_INTERCEPTORS, useClass: ApiRootInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptorService, multi: true },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
